import Button from '@pelckmans/elementis/components/button';
import Dialog from '@pelckmans/elementis/components/dialog';
import Heading from '@pelckmans/elementis/components/heading';
import UploadArea from '@pelckmans/elementis/components/upload-area';
import ArrowLeftRightIcon from '@pelckmans/elementis/icons/arrow-left-right';
import ZoomInIcon from '@pelckmans/elementis/icons/zoom-in';
import ZoomOutIcon from '@pelckmans/elementis/icons/zoom-out';
import { ChangeEvent, Dispatch, SetStateAction, useCallback, useState } from 'react';
import Cropper, { Area, Point } from 'react-easy-crop';
import { useTranslation } from 'react-i18next';
import Slider from 'react-slider';
import { FileData } from '../modals/SelectAvatarModal';

const BASE = 'PAGES.PROFILE.MODALS.SELECT_AVATAR.TABS.CUSTOM.BODY';

const MAXIMUM_SIZE = '2Mb';
const ALLOWED_TYPES_TEXT = 'png, jpg, jpeg, gif';

const ACCEPT_TYPES = '.png, .jpg, .jpeg, .gif';

const validFileType = (file: File) => {
  return ['image/jpg', 'image/jpeg', 'image/png', 'image/gif'].includes(file.type);
};

const validFileSize = (file: File) => {
  return file.size <= 2048 * 1024;
};

interface Props {
  customAvatar?: FileData;
  setCustomAvatar: Dispatch<SetStateAction<FileData | undefined>>;
  setCroppedAreaPixels: Dispatch<SetStateAction<Area | undefined>>;
  errorMessage?: string;
  setErrorMessage: Dispatch<SetStateAction<string | undefined>>;
}

export default function CustomAvatarTab({
  customAvatar,
  setCustomAvatar,
  setCroppedAreaPixels,
  errorMessage,
  setErrorMessage,
}: Props) {
  const { t } = useTranslation();
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1.05);

  const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (!file) return;

    if (!validFileType(file)) {
      return setErrorMessage(t('PAGES.PROFILE.MODALS.SELECT_AVATAR.ERROR.TYPE'));
    }

    if (!validFileSize(file)) {
      return setErrorMessage(t('PAGES.PROFILE.MODALS.SELECT_AVATAR.ERROR.SIZE'));
    }

    const url = URL.createObjectURL(file);
    setCustomAvatar({ file, url });
  };

  const handleCropComplete = useCallback(
    (_: Area, croppedAreaPixels: Area) => {
      setCroppedAreaPixels(croppedAreaPixels);
    },
    [setCroppedAreaPixels],
  );

  return (
    <Dialog.Body>
      <div className="custom-avatar-modal-body" data-testid="custom-avatar-modal-body">
        <div className="custom-avatar-modal-body--title">
          <Heading level={4} removeSpacing="all" className="modal-content-text-title">
            {t(`${BASE}.LABEL`)}
          </Heading>
        </div>
        {customAvatar ? (
          <>
            <Cropper
              image={customAvatar.url}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropSize={{ width: 400, height: 400 }}
              onCropChange={setCrop}
              onCropComplete={handleCropComplete}
              cropShape="round"
              objectFit="horizontal-cover"
              onZoomChange={setZoom}
              showGrid={false}
            />
            <div className="controls">
              <Button onClick={() => setCustomAvatar(undefined)} size="sm" prominence="ghost">
                {t(`${BASE}.UPLOAD_AREA.CONTROLS.CHANGE_IMAGE`)}
              </Button>
              <span className="slider">
                <Button
                  prominence="ghost"
                  startIcon={<ZoomOutIcon />}
                  aria-label={t(`${BASE}.UPLOAD_AREA.CONTROLS.ZOOM_OUT.ARIA_LABEL`)}
                  title={t(`${BASE}.UPLOAD_AREA.CONTROLS.ZOOM_OUT.TOOLTIP`)}
                  onClick={() => setZoom(prev => Math.max(1, prev - 0.2))}
                />
                <Slider
                  className="slider-zoom"
                  thumbClassName="slider-zoom__thumb"
                  trackClassName="slider-zoom__track"
                  value={zoom}
                  onChange={value => setZoom(value)}
                  min={1}
                  max={3}
                  step={0.01}
                  defaultValue={0}
                  renderThumb={({ key, ...props }) => (
                    <Button
                      prominence="neutral"
                      startIcon={<ArrowLeftRightIcon />}
                      title={t(`${BASE}.UPLOAD_AREA.CONTROLS.THUMB_BUTTON.TOOLTIP`)}
                      key={key}
                      size="sm"
                      {...(props as any)}
                      aria-label={t(`${BASE}.UPLOAD_AREA.CONTROLS.THUMB_BUTTON.ARIA_LABEL`)}
                    />
                  )}
                />
                <Button
                  prominence="ghost"
                  startIcon={<ZoomInIcon />}
                  aria-label={t(`${BASE}.UPLOAD_AREA.CONTROLS.ZOOM_IN.ARIA_LABEL`)}
                  onClick={() => setZoom(prev => Math.min(3, prev + 0.2))}
                  title={t(`${BASE}.UPLOAD_AREA.CONTROLS.ZOOM_IN.TOOLTIP`)}
                />
              </span>
            </div>
          </>
        ) : (
          <UploadArea
            allowedFileType={ALLOWED_TYPES_TEXT}
            maxFileSize={MAXIMUM_SIZE}
            placeholder={t(`${BASE}.UPLOAD_AREA.PLACEHOLDER`)}
            placeholderSubText={t(`${BASE}.UPLOAD_AREA.PLACEHOLDER_SUB_TEXT`)}
            onChange={handleImageChange}
            error={!!errorMessage}
            errorText={errorMessage}
            accept={ACCEPT_TYPES}
          />
        )}
      </div>
    </Dialog.Body>
  );
}
